
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { PEOQuote } from '@/types/quotes.types';
import { useToast } from '@/hooks/use-toast';

export const useAdminQuotes = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const fetchAllQuotes = async (): Promise<PEOQuote[]> => {
    const { data, error } = await supabase
      .from('peo_quotes')
      .select(`
        *,
        peo_providers(name),
        applications(user_id, company_legal_name, first_name, last_name)
      `);

    if (error) throw error;

    return data.map((quote: any) => ({
      ...quote,
      provider_name: quote.peo_providers?.name || 'Unknown Provider',
      company_name: quote.applications?.company_legal_name || 'Unknown Company',
      applicant_name: `${quote.applications?.first_name || ''} ${quote.applications?.last_name || ''}`.trim() || 'Unknown Applicant'
    }));
  };

  const fetchApplicationsWithoutAcceptedQuotes = async () => {
    // Get all submitted applications
    const { data: applications, error: appError } = await supabase
      .from('applications')
      .select('id, company_legal_name, first_name, last_name, status')
      .eq('status', 'submitted');

    if (appError) throw appError;

    // Get applications that have accepted quotes
    const { data: acceptedQuotes, error: quoteError } = await supabase
      .from('peo_quotes')
      .select('application_id')
      .eq('status', 'accepted');

    if (quoteError) throw quoteError;

    // Create a set of application IDs that have accepted quotes
    const acceptedApplicationIds = new Set(acceptedQuotes.map(quote => quote.application_id));

    // Filter out applications that have accepted quotes
    const availableApplications = applications.filter(app => !acceptedApplicationIds.has(app.id));

    return availableApplications;
  };

  const uploadQuoteFile = async (file: File, applicationId: string, providerId: string) => {
    const fileExt = file.name.split('.').pop();
    const fileName = `${applicationId}/${providerId}/${Date.now()}.${fileExt}`;
    const filePath = `${fileName}`;

    const { error: uploadError, data } = await supabase.storage
      .from('peo-quotes')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false
      });

    if (uploadError) throw uploadError;

    const { data: { publicUrl } } = supabase.storage
      .from('peo-quotes')
      .getPublicUrl(filePath);

    return publicUrl;
  };

  const createQuote = async ({
    applicationId,
    providerId,
    summary,
    file,
    monthlyAmount,
    annualAmount,
    adminNotes,
    validUntil
  }: {
    applicationId: string;
    providerId: string;
    summary: string;
    file?: File;
    monthlyAmount?: number;
    annualAmount?: number;
    adminNotes?: string;
    validUntil?: Date;
  }) => {
    let fileUrl = '';
    
    // Only upload file if provided
    if (file) {
      fileUrl = await uploadQuoteFile(file, applicationId, providerId);
    }

    // Get the current user
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      throw new Error('User not authenticated');
    }

    // Create quote record
    const { data, error } = await supabase
      .from('peo_quotes')
      .insert({
        application_id: applicationId,
        provider_id: providerId,
        quote_summary: summary,
        quote_file_url: fileUrl || null,
        admin_notes: adminNotes || null,
        monthly_cost: monthlyAmount || null,
        annual_cost: annualAmount || null,
        created_by: user.id,
        valid_until: validUntil ? validUntil.toISOString() : null
      })
      .select();

    if (error) {
      console.error("Error creating quote:", error);
      throw error;
    }
    
    // Send notification to the PEO provider
    try {
      await supabase.functions.invoke('notify-quote-assignment', {
        body: {
          quoteId: data[0].id,
          providerId,
          applicationId
        }
      });
    } catch (notificationError) {
      console.error("Failed to send notification:", notificationError);
      // Don't fail the quote creation if notification fails
    }
    
    return data[0];
  };

  const deleteQuote = async (quoteId: string) => {
    // Get the file path first
    const { data: quote, error: fetchError } = await supabase
      .from('peo_quotes')
      .select('quote_file_url')
      .eq('id', quoteId)
      .single();

    if (fetchError) throw fetchError;

    // Delete the file from storage
    if (quote.quote_file_url) {
      const filePathMatch = quote.quote_file_url.match(/\/peo-quotes\/([^?]+)/);
      if (filePathMatch && filePathMatch[1]) {
        const filePath = filePathMatch[1];
        const { error: storageError } = await supabase.storage
          .from('peo-quotes')
          .remove([filePath]);
        
        if (storageError) {
          console.error('Error deleting file:', storageError);
          // Continue with quote deletion even if file deletion fails
        }
      }
    }

    // Delete the quote record
    const { error } = await supabase
      .from('peo_quotes')
      .delete()
      .eq('id', quoteId);

    if (error) throw error;
    return true;
  };

  const { data: allQuotes = [], isLoading, error, refetch } = useQuery({
    queryKey: ['admin-peo-quotes'],
    queryFn: fetchAllQuotes
  });

  const { data: availableApplications = [], isLoading: isLoadingApplications } = useQuery({
    queryKey: ['available-applications'],
    queryFn: fetchApplicationsWithoutAcceptedQuotes
  });

  const { mutate: addQuote, isPending: isAddingQuote } = useMutation({
    mutationFn: createQuote,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-peo-quotes'] });
      queryClient.invalidateQueries({ queryKey: ['available-applications'] });
      toast({
        title: "Quote added",
        description: "The quote has been successfully added and the provider has been notified.",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: `Failed to add quote: ${error.message}`,
        variant: "destructive",
      });
    }
  });

  const { mutate: removeQuote, isPending: isDeletingQuote } = useMutation({
    mutationFn: deleteQuote,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-peo-quotes'] });
      queryClient.invalidateQueries({ queryKey: ['available-applications'] });
      toast({
        title: "Quote deleted",
        description: "The quote has been successfully deleted.",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: `Failed to delete quote: ${error.message}`,
        variant: "destructive",
      });
    }
  });

  return {
    allQuotes,
    isLoading,
    error,
    refetch,
    addQuote,
    isAddingQuote,
    removeQuote,
    isDeletingQuote,
    availableApplications,
    isLoadingApplications
  };
};
